import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import orderService from '../../services/orderService';

// ======================= ASYNC THUNKS (ACTIONS) =======================
// Each thunk calls one existing API route through orderService and rejects with the server's message.
export const createOrder = createAsyncThunk(
  'orders/createOrder',
  async (orderData, { rejectWithValue }) => {
    try {
      const response = await orderService.createOrder(orderData);
      return response.data || response;
    } catch (error) {
      // Surface the backend's specific message for the UI toast
      const message =
        error?.message ||
        error?.response?.data?.message ||
        'Failed to create order';
      return rejectWithValue(message);
    }
  }
);
// The service throws the server's error body ({ success: false, message }); fall back to a fixed text.
const errorMessage = (error, fallback) => error?.message || error?.response?.data?.message || fallback;

// Lifecycle routes answer { success, message } and sometimes data (the updated order). The payload always
// carries the order id so the reducers can patch the order in place.
const withOrderId = (orderId, response) => ({
  id: orderId,
  ...(response?.data && typeof response.data === 'object' && !Array.isArray(response.data) ? response.data : {}),
});

// GET /api/orders?role=&status=&limit=&skip= → { data: orders[], total }
export const fetchUserOrders = createAsyncThunk(
  'orders/fetchUserOrders',
  async ({ role, status, page = 1, limit = 10 } = {}, { rejectWithValue }) => {
    try {
      const response = await orderService.getUserOrders({ role, status, limit, skip: (page - 1) * limit });
      const total = Number(response?.total) || 0;
      return {
        orders: Array.isArray(response?.data) ? response.data : [],
        listType: role === 'buyer' || role === 'seller' ? role : 'all',
        pagination: { page, limit, total, pages: Math.ceil(total / limit) },
      };
    } catch (error) {
      return rejectWithValue(errorMessage(error, 'Failed to fetch orders'));
    }
  }
);

// GET /api/orders/:id (id or order number) → data: the order with isBuyer / isSeller
export const fetchOrderById = createAsyncThunk('orders/fetchOrderById', async (orderId, { rejectWithValue }) => {
  try {
    const response = await orderService.getOrderById(orderId);
    return response?.data;
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Failed to fetch order'));
  }
});

// POST /api/orders/:id/pay (payOrderEscrow) → data: the paid order
export const payOrder = createAsyncThunk('orders/payOrder', async (orderId, { rejectWithValue }) => {
  try {
    return withOrderId(orderId, await orderService.payOrder(orderId));
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Payment failed'));
  }
});

// PATCH /api/orders/:id/start
export const startOrder = createAsyncThunk('orders/startOrder', async (orderId, { rejectWithValue }) => {
  try {
    await orderService.startOrder(orderId);
    return { id: orderId, status: 'in_progress' };
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Failed to start order'));
  }
});

// POST /api/orders/:id/deliver (needs at least one delivered file URL)
export const deliverOrder = createAsyncThunk('orders/deliverOrder', async ({ orderId, deliveryData }, { rejectWithValue }) => {
  try {
    await orderService.deliverOrder(orderId, deliveryData);
    return { id: orderId, status: 'delivered' };
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Failed to deliver order'));
  }
});

// POST /api/orders/:id/revision
export const requestRevision = createAsyncThunk('orders/requestRevision', async ({ orderId, reason }, { rejectWithValue }) => {
  try {
    await orderService.requestRevision(orderId, reason);
    return { id: orderId, status: 'revision_requested' };
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Failed to request revision'));
  }
});

// No server route: accepting a revision is not implemented on the API (routes/orders.js keeps it commented
// out), so this never calls the network and the order page does not offer it.
export const acceptRevision = createAsyncThunk('orders/acceptRevision', async (_orderId, { rejectWithValue }) =>
  rejectWithValue('Accepting a revision is not available yet.')
);

// POST /api/orders/:id/accept → data: the completed order (escrow released)
export const acceptDelivery = createAsyncThunk('orders/acceptDelivery', async (orderId, { rejectWithValue }) => {
  try {
    return withOrderId(orderId, await orderService.acceptDelivery(orderId));
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Failed to accept delivery'));
  }
});

// POST /api/orders/:id/cancellation
export const requestCancellation = createAsyncThunk('orders/requestCancellation', async ({ orderId, reason }, { rejectWithValue }) => {
  try {
    await orderService.requestCancellation(orderId, reason);
    return { id: orderId, status: 'cancellation_pending', cancellationStatus: 'pending_consent' };
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Failed to request cancellation'));
  }
});

// PATCH /api/orders/:id/cancellation/approve
export const approveCancellation = createAsyncThunk('orders/approveCancellation', async (orderId, { rejectWithValue }) => {
  try {
    await orderService.approveCancellation(orderId);
    return { order: { id: orderId, status: 'cancelled', cancellationStatus: 'approved' } };
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Failed to approve cancellation'));
  }
});

// PATCH /api/orders/:id/cancellation/deny (the order goes back to its previous status; reload to see it)
export const denyCancellation = createAsyncThunk('orders/denyCancellation', async (orderId, { rejectWithValue }) => {
  try {
    await orderService.denyCancellation(orderId);
    return { id: orderId, cancellationStatus: 'denied' };
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Failed to deny cancellation'));
  }
});

// POST /api/disputes/create
export const raiseDispute = createAsyncThunk('orders/raiseDispute', async ({ orderId, disputeData }, { rejectWithValue }) => {
  try {
    await orderService.raiseDispute(orderId, disputeData);
    return { id: orderId, status: 'disputed' };
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Failed to raise dispute'));
  }
});
export const fetchOrderStatistics = createAsyncThunk(
  'orders/fetchOrderStatistics',
  async (role, { rejectWithValue }) => {
    try {
      const response = await orderService.getOrderStatistics(role);
      return response.data || response;
    } catch (error) {
      const message =
        error?.message ||
        error?.response?.data?.message ||
        'Failed to fetch order statistics';
      return rejectWithValue(message);
    }
  }
);


// ======================= INITIAL STATE =======================
const initialState = {
    orders: [],
    currentOrder: null,
    buyerOrders: [],
    sellerOrders: [],
    statistics: {
        buyer: null,
        seller: null
    },
    loading: false,
    error: null,
    pagination: {
        page: 1,
        limit: 10,
        total: 0,
        pages: 0
    },
    realtimeUpdate: null,
};


// ======================= HELPER FUNCTION =======================
const updateOrderState = (state, updatedOrder) => {
    const updatedId = updatedOrder?.id || updatedOrder?._id;
    if (!updatedOrder || !updatedId) return;

    if (state.currentOrder && (state.currentOrder.id || state.currentOrder._id) === updatedId) {
        state.currentOrder = { ...state.currentOrder, ...updatedOrder };
    }

    const lists = [state.orders, state.buyerOrders, state.sellerOrders];
    lists.forEach(list => {
        const index = list.findIndex(order => (order.id || order._id) === updatedId);
        if (index !== -1) {
            list[index] = { ...list[index], ...updatedOrder };
        }
    });
};


// ======================= ORDERS SLICE =======================
const ordersSlice = createSlice({
    name: 'orders',
    initialState,
    reducers: {
        clearCurrentOrder: (state) => {
            state.currentOrder = null;
        },
        clearError: (state) => {
            state.error = null;
        },
        updateOrderInList: (state, action) => {
            updateOrderState(state, action.payload);
        },
        addOrderToList: (state, action) => {
            state.orders.unshift(action.payload);
        },
        
        // SOCKET.IO REAL-TIME UPDATES (RENAMED from orderStatusUpdate to updateOrderStatus)
        updateOrderStatus: (state, action) => { // FIX APPLIED HERE: Renamed reducer function
            const { orderId, status } = action.payload;
            updateOrderState(state, { id: orderId, _id: orderId, status, updatedAt: new Date().toISOString() });
            state.realtimeUpdate = { type: 'status', orderId, status, timestamp: Date.now() };
        },
        
        orderLocationUpdate: (state, action) => {
            const { orderId, location, eta, distance } = action.payload;
            
            if (state.currentOrder && (state.currentOrder.id || state.currentOrder._id) === orderId) {
                state.currentOrder.giggerLocation = location;
                state.currentOrder.giggerETA = eta;
            }
            
            state.realtimeUpdate = { 
                type: 'location', 
                orderId, 
                location, 
                eta, 
                distance,
                timestamp: Date.now()
            };
        }
    },
    
    // ======================= ASYNC THUNK FULFILLMENT LOGIC (FIXED ORDER) =======================
    extraReducers: (builder) => {
        builder
            // 1. ALL addCase CALLS MUST COME FIRST

            // 1a. Creation
            .addCase(createOrder.fulfilled, (state, action) => {
                state.loading = false;
                state.currentOrder = action.payload;
            })

            // 1b. Fetching
            .addCase(fetchUserOrders.fulfilled, (state, action) => {
                state.loading = false;
                const { orders, pagination, listType } = action.payload || {}; // Destructure with default empty object
                
                if (listType === 'buyer') {
                    state.buyerOrders = orders || [];
                } else if (listType === 'seller') {
                    state.sellerOrders = orders || [];
                } else {
                    state.orders = orders || [];
                }
                
                state.pagination = pagination || initialState.pagination;
            })
            .addCase(fetchOrderById.fulfilled, (state, action) => {
                state.loading = false;
                state.currentOrder = action.payload;
            })

            // 1c. Status/Update Actions (All use updateOrderState helper)
            .addCase(startOrder.fulfilled, (state, action) => { 
                state.loading = false;
                updateOrderState(state, action.payload); 
            })
            .addCase(deliverOrder.fulfilled, (state, action) => { 
                state.loading = false;
                updateOrderState(state, action.payload); 
            })
            .addCase(requestRevision.fulfilled, (state, action) => { 
                state.loading = false;
                updateOrderState(state, action.payload); 
            })
            .addCase(acceptRevision.fulfilled, (state, action) => { 
                state.loading = false;
                updateOrderState(state, action.payload); 
            })
            .addCase(acceptDelivery.fulfilled, (state, action) => { 
                state.loading = false;
                updateOrderState(state, action.payload); 
            })
            .addCase(requestCancellation.fulfilled, (state, action) => { 
                state.loading = false;
                updateOrderState(state, action.payload); 
            })
            .addCase(approveCancellation.fulfilled, (state, action) => { 
                state.loading = false;
                updateOrderState(state, action.payload?.order); // Use optional chaining for safety
            })
            .addCase(raiseDispute.fulfilled, (state, action) => {
                state.loading = false;
                updateOrderState(state, action.payload);
            })
            .addCase(payOrder.fulfilled, (state, action) => {
                state.loading = false;
                updateOrderState(state, action.payload);
            })
            .addCase(denyCancellation.fulfilled, (state, action) => {
                state.loading = false;
                updateOrderState(state, action.payload);
            })
            
            // 1d. Statistics (THE CRITICAL FIX)
            .addCase(fetchOrderStatistics.fulfilled, (state, action) => {
                state.loading = false;
                // FIX: Check if action.payload exists before trying to read 'role'
                if (action.payload) {
                    if (action.payload.role === 'buyer') {
                        state.statistics.buyer = action.payload;
                    } else if (action.payload.role === 'seller') {
                        state.statistics.seller = action.payload;
                    }
                }
            })

            // 2. ALL addMatcher CALLS MUST COME AFTER

            // Only this slice's thunks ('orders/…'): another slice's pending action (e.g. messages/…) used to
            // set loading here with nothing to clear it, leaving the order pages on their spinner.
            // Centralized loading handler for all pending orders thunks
            .addMatcher(
                (action) => action.type.startsWith('orders/') && action.type.endsWith('/pending'),
                (state) => {
                    state.loading = true;
                    state.error = null;
                }
            )
            // Every fulfilled orders thunk ends the loading state, including ones without their own case
            .addMatcher(
                (action) => action.type.startsWith('orders/') && action.type.endsWith('/fulfilled'),
                (state) => {
                    state.loading = false;
                }
            )
            // Centralized error handler for all rejected orders thunks
            .addMatcher(
                (action) => action.type.startsWith('orders/') && action.type.endsWith('/rejected'),
                (state, action) => {
                    state.loading = false;
                    state.error = action.payload || action.error.message || 'An unknown error occurred.';
                }
            );
    }
});

export const { 
    clearCurrentOrder, 
    clearError, 
    updateOrderInList, 
    addOrderToList,
    updateOrderStatus, // FIX APPLIED HERE: Renamed exported action
    orderLocationUpdate
} = ordersSlice.actions;

export default ordersSlice.reducer;